import { boundedTone, level, type Tone } from './state.ts';
import { StatusDot } from './StatusDot.tsx';
import { duration, utc, local, NO_VALUE } from './format.ts';

/** A cadence in the console's own idiom: `5s`, `20s`, `2m`. */
function everyShort(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 90 ? `${s}s` : `${Math.round(s / 60)}m`;
}
import styles from './Freshness.module.css';

/* ==========================================================================
   The trust surface, per panel.

   THE BAR §6 is blunt about this: "the operator must never have to ask 'is
   this now, and is this real?' Both answers are on screen at all times, PER
   PANEL." A single connection light in the app header cannot answer it — a
   console routinely shows one panel streaming while the one beside it is
   scrubbed to an hour ago, or a map that is live while a camera has stalled.

   Two primitives, both sized for a Panel's `meta` slot:

     LiveTag   is this panel showing now, or history?
     Latency   how far behind is this particular stream?

   Neither paints "fine" in a colour. A panel that is live is steel and a
   latency inside its budget is steel; amber and red are spent on the panel
   that has stopped being current, which is the one the operator needs to
   find among six.
   ========================================================================== */

export function LiveTag({
  live,
  /** The instant being shown when `live` is false — a scrub position. */
  at,
  /** Word for the live state: `Live`, `Polling`, `Contact`. Surfaces fed by
      the console's own transport take it from `useFeedWord()` rather than
      choosing, so one frame never names one fact two ways. */
  label = 'Live',
  /** Only ever true where a stream is genuinely pushing right now. */
  pulse = false,
  /** The panel's cadence, ms — printed after the word (`Polling 5s`). A
      freshness badge that says a panel is current without saying how often
      makes the operator guess how long "current" lasts. */
  every,
  /** How long the panel has been showing a photograph, ms. Printed only when
      `live` is false. It is the number an operator reads first — "how long
      have I been looking at this" — and it used to be composed alongside this
      badge by a wrapper in `workspaces/commands` because the badge could not
      say it. */
  age,
  ageTitle,
}: {
  live: boolean;
  at?: string | number | null | undefined;
  label?: string | undefined;
  pulse?: boolean | undefined;
  every?: number | undefined;
  age?: number | null | undefined;
  ageTitle?: string | undefined;
}) {
  const iso =
    at === null || at === undefined
      ? null
      : typeof at === 'number'
        ? new Date(at).toISOString()
        : at;

  return (
    <span
      className={styles.live}
      data-tone={live ? 'nominal' : 'warn'}
      data-live={live}
      data-pulse={pulse && live}
      title={
        live
          ? `${label} — this panel is showing the current value`
            + (every === undefined || every <= 0 ? '' : `, re-read every ${everyShort(every)}`)
          : iso === null
            ? 'This panel is showing history, not the current value'
            : `Showing ${utc(iso)}  ·  ${local(iso)}`
      }
    >
      {/* A dot, not a glyph. §5's encoding hierarchy puts the 6px dot above a
          picture, this dot means the same thing on every other surface in the
          app, and — decisively — a struck antenna is a smudge at the 12px this
          badge renders at, while a filled-vs-hollow dot is unambiguous at 6.
          Fill state carries the fact; colour is the second signal, never the
          only one. */}
      <StatusDot tone={live ? 'nominal' : 'warn'} hollow={!live} pulse={pulse && live} />
      <span className={styles.liveWord}>{live ? label : `${label} off`}</span>
      {live && every !== undefined && every > 0 && (
        <span className={styles.liveEvery}>{everyShort(every)}</span>
      )}
      {!live && iso !== null && (
        <time className={styles.liveAt} dateTime={iso}>
          {utc(iso).slice(11)}
        </time>
      )}
      {!live && age != null && (
        <span className={styles.liveAge} title={ageTitle}>
          {duration(age)}
        </span>
      )}
    </span>
  );
}

/* --------------------------------------------------------------------------
   Latency — `38.6 ms ▮▮▮▮▯`.

   Per stream, never per page: a wall of camera tiles answers freshness tile
   by tile (THE BAR §5). The five bars are a shape you can read without
   reading the number, which is what makes it usable in a header band; the
   number is the truth.
   -------------------------------------------------------------------------- */

const BARS = 5;

export function Latency({
  ms,
  /** Milliseconds. Above `warn` the readout goes amber, above `bad` red. */
  warn = 150,
  bad = 400,
  label = 'Link',
  /** Hide the bars where the row is already dense — the number still reads. */
  bars = true,
  /** Honoured only inside the budget; a breached one always wins. */
  tone,
}: {
  ms: number | null | undefined;
  warn?: number | undefined;
  bad?: number | undefined;
  label?: string | undefined;
  bars?: boolean | undefined;
  tone?: Tone | undefined;
}) {
  const known = ms != null && !Number.isNaN(ms) && ms >= 0;
  const lv = level(known ? ms : null, warn, bad, true);
  const resolved = boundedTone(lv, tone);

  /* Bars fall off as latency climbs: full inside the budget, one bar left at
     the hard limit, none past it. Quality, not magnitude — the number carries
     magnitude and the bars carry "can I drive on this". */
  const filled = !known
    ? 0
    : ms <= warn
      ? BARS
      : ms >= bad
        ? 1
        : BARS - Math.ceil(((ms - warn) / (bad - warn)) * (BARS - 1));

  return (
    <span
      className={styles.latency}
      data-tone={resolved}
      data-toned={lv === 'warn' || lv === 'bad'}
      data-unknown={!known}
      title={
        known
          ? `${label} round trip ${ms.toFixed(1)} ms (budget ${warn} ms, limit ${bad} ms)`
          : `${label} — no measurement`
      }
    >
      <span className={styles.latencyValue}>
        {known ? ms.toFixed(known && ms < 100 ? 1 : 0) : NO_VALUE}
        {known && <span className={styles.latencyUnit}>ms</span>}
      </span>
      {bars && (
        <span
          className={styles.bars}
          role="img"
          aria-label={known ? `${label} ${ms.toFixed(0)} milliseconds` : `${label} no measurement`}
        >
          {Array.from({ length: BARS }, (_, i) => (
            <span key={i} className={styles.bar} data-on={i < filled} data-h={i} />
          ))}
        </span>
      )}
    </span>
  );
}
